import React from 'react';
import { Button, phone } from '@parsina/framework';
import { Clock } from 'lucide-react';
import { C, CT, statusColors } from '../constants/theme';
import { useTranslation } from '../i18n/I18nProvider.jsx';
import { waitedMinutes, visitOverrun, WAIT_WARN_MIN, displayName, isDone } from '../lib/appointments.js';
import { durationLabel } from '../lib/durationLabel.js';
import StatusPill from './StatusPill.jsx';
import PatientAvatar from './PatientAvatar.jsx';

// One day-sheet row.
//
// TWO actions, and the distinction between them is the point:
//
//   «پرونده»   opens the patient's RECORD — everything ever recorded.
//   «جلسه»     opens THIS APPOINTMENT's workspace (docs/dent-visit.md).
//
// Neither writes to scheduling, which is still the rule this app is built on:
// «حاضر شد» belongs to the clinic desk and there is no server behind it here.
// Opening a visit is a READ — the workspace itself refuses to write to a
// completed or cancelled appointment — so it is not the «شروع ویزیت» this
// comment used to rule out. That one changes an appointment's status; this one
// changes which screen the dentist is on.
//
// A finished row keeps both: yesterday's visit is read-only, not hidden.

// An open visit escalates on ONE vocabulary: normal (in-visit blue) → over
// (past its booked slot, amber) → critical (past twice its slot, red). All from
// tokens; there is no local palette anywhere in this file.
const OVERRUN_CHIP = {
  normal: { bg: statusColors.in_visit.bg, color: statusColors.in_visit.text },
  over: { bg: CT.warnSoft, color: CT.warnInk },
  critical: { bg: CT.dangerSoft, color: CT.dangerInk },
};

const NEUTRAL_BG = CT.surface2;

// The status pill moved to `src/components/StatusPill.jsx`: the mobile card
// renders the identical pill, and two copies of "what is «arrived» called" is
// how the two surfaces would come to disagree.

const ClockGlyph = () => (
  <Clock
    size={11}
    strokeWidth={2.4}
    style={{ display: 'inline-block', verticalAlign: '-1.5px', marginInlineEnd: 3 }}
  />
);

const QueuePos = ({ pos }) => {
  const { t } = useTranslation();
  const first = pos === 1;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        borderRadius: 6,
        padding: '2px 7px',
        whiteSpace: 'nowrap',
        color: first ? statusColors.arrived.text : C.textSecondary,
        background: first ? statusColors.arrived.bg : NEUTRAL_BG,
        border: `1px solid ${first ? 'transparent' : C.border}`,
        flexShrink: 0,
      }}
    >
      {first ? t('today.queue.positionNext') : t('today.queue.position', { position: pos })}
    </span>
  );
};

const AppointmentRow = ({ apt, queuePos, nowMs, onOpen, onOpenVisit }) => {
  const { t, formatDigits } = useTranslation();
  const [hovered, setHovered] = React.useState(false);

  const waited = apt.status === 'arrived' ? waitedMinutes(apt, nowMs) : null;
  const { elapsedMin, level } =
    apt.status === 'in_visit' ? visitOverrun(apt, nowMs) : { elapsedMin: null, level: 'normal' };
  const overChip = OVERRUN_CHIP[level];
  const dotColor = (statusColors[apt.status] || {}).text || C.textMuted;

  const name = displayName(apt);
  // A «ثبت سریع» row has no patient record to open — placeholderLabel carries
  // the name INSTEAD of patientId, and exactly one of the two is ever set.
  const walkIn = !apt.patientId;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderRadius: 12,
        marginBottom: 8,
        background: hovered ? CT.surface2 : CT.surface,
        border: `1.5px solid ${hovered ? C.border : NEUTRAL_BG}`,
        transition: 'all 0.15s',
      }}
    >
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />

      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: C.textPrimary,
          minWidth: 44,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {apt.time ? formatDigits(apt.time) : t('common.labels.none')}
      </div>

      <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />

      <PatientAvatar name={name} src={apt.patient?.avatarUrl} size={32} bg={C.navy} />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name || t('today.row.unknownPatient')}
        </div>
        {apt.patient?.phone && (
          // A phone number is a Latin-digit run: it must read left-to-right
          // inside an RTL line, and `isolate` stops the punctuation around it
          // being dragged to the wrong end.
          <span
            dir="ltr"
            style={{
              fontSize: 11,
              color: C.textMuted,
              unicodeBidi: 'isolate',
              whiteSpace: 'nowrap',
            }}
          >
            {formatDigits(phone.forDisplay(apt.patient.phone))}
          </span>
        )}
        {walkIn && (
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 800,
              borderRadius: 5,
              padding: '1px 6px',
              flexShrink: 0,
              background: NEUTRAL_BG,
              color: C.textSecondary,
            }}
          >
            {t('today.row.walkIn')}
          </span>
        )}
      </div>

      {waited != null && (
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            borderRadius: 20,
            padding: '2px 9px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            background: waited > WAIT_WARN_MIN ? CT.warnSoft : NEUTRAL_BG,
            color: waited > WAIT_WARN_MIN ? CT.warnInk : C.textSecondary,
          }}
        >
          <ClockGlyph />
          {durationLabel(t, 'today.queue.waited', waited)}
        </span>
      )}

      {elapsedMin != null && (
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            borderRadius: 20,
            padding: '2px 9px',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            background: overChip.bg,
            color: overChip.color,
          }}
        >
          <ClockGlyph />
          {durationLabel(t, 'today.queue.elapsed', elapsedMin)}
        </span>
      )}

      {queuePos != null && <QueuePos pos={queuePos} />}

      <StatusPill status={apt.status} />

      {!walkIn && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {/* جلسهٔ درمان is the PRIMARY action on a row that is still live: the
              day sheet is a list of visits, and the visit is where the work
              happens. On a finished row both go tonal — it is history. */}
          {onOpenVisit && (
            <Button
              size="sm"
              variant={isDone(apt.status) ? 'subtle' : 'default'}
              onClick={() => onOpenVisit(apt)}
              aria-label={t('today.row.visitFor', { name: name || t('today.row.unknownPatient') })}
              className="shrink-0"
            >
              {t('today.row.visit')}
            </Button>
          )}
          <Button
            size="sm"
            variant={onOpenVisit ? 'outline' : (isDone(apt.status) ? 'subtle' : 'default')}
            onClick={() => onOpen?.(apt)}
            aria-label={t('today.row.openFor', { name: name || t('today.row.unknownPatient') })}
            className="shrink-0"
          >
            {t('today.row.open')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AppointmentRow;
